import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { MobileHeader } from "components/Chat/ChatHeader/ChatHeader";
import { MessageBubble } from "components/Chat/MessageBubble/MessageBubble";
import { TypingLoader } from "components/TypingLoader/TypingLoader";
import { MobileChatInstructions, ChatInstructions } from "components/Chat/ChatInstructions/ChatInstructions";
import ChatTimeWarningProgress from "components/Chat/ChatTimeWarningProgress/ChatTimeWarningProgress";
import ChatInput from "components/Chat/ChatInput/ChatInput";
import SubmitLoader from "components/Chat/SubmitLoader";
import { API_RESPONSE_EVENT, API_SAVE_CHAT_EVENT } from "constant";
import { GC } from "services/gamecentreService";
import { storage } from "services/storage";
import { getChatMessage } from "utils/helper";

export const Chat = ({
  question,
  completionPrompt,
  messages,
  setMessages,
  onChatSubmit,
  chatStarted,
  handleStartChat,
  totalTime,
  timeLeft,
  endChatBtn,
  isSubmitting,
}) => {
  const inIframe = window.location !== window.parent.location;
  const allowSubmit = messages && messages.length >= 3;

  const [loadingReply, setLoadingReply] = useState(false);
  const [errorOccured, setErrorOccured] = useState(false);
  const [mobileInstructionsVisible, setMobileInstructionsVisible] = useState(false)

  const inputRef = useRef(null);

  const getBotReply = async (messages) => {
    if (inIframe) {
      setLoadingReply(true);
      setErrorOccured(false);

      // sending all messages and promptId to GC for API call
      GC.sendGameDataSaveMessage({
        messages,
        promptId: completionPrompt,
        generateChatReply: true,
      });
    } else {
      // Bypass auth, use valid authToken in env to get this to work
      const chatResponse = await getChatMessage({ promptId: completionPrompt, messages });
      if (chatResponse) {
        storage.set.chats(chatResponse);
        window.dispatchEvent(new Event(API_RESPONSE_EVENT));
      } 
    }
  };

  useEffect(() => {
    // * scrolling chat window to bottom on mount
    if (!chatStarted) return;
    const chatWindow = document.getElementById("chat-scroll-section");
    const scrollContainer = document.querySelector('.scroll-container');

    // Mobile scroll
    scrollContainer?.scrollTo({ top: scrollContainer?.scrollHeight, behavior: "smooth" })
    // Desktop scroll
    chatWindow?.scrollTo({ top: chatWindow?.scrollHeight, behavior: "smooth" })

  }, [messages, chatStarted]);

  // When input state is no longer disabled, focus on input to show cursor blinking.
  // Otherwise focusing ref before async setLoadingReply -> no cursor blinking.
  useEffect(() => {
    if (!loadingReply) inputRef?.current?.focus();
  }, [loadingReply, chatStarted]);

  useEffect(() => {
    // Check not required anymore,
    // Fetching chat with valid authToken in case not loaded in iframe

    // if (!inIframe) {
      // Not calling gamecenter apis if not loaded in it.
      // getBotReply(messages)
      // return;
    // }

    // * getting initial message from chatbot based on the completion prompt
    // Adding timeout to handle React.18 intentinonal re-render on development
    const apiTimeoutRef = setTimeout(() => {
      getBotReply(messages);
    }, 0);

    const newMessageHandler = () => {
      const newMessages = storage.get.chats();
      if (newMessages && Array.isArray(newMessages)) {
        setMessages(storage.get.chats());
        setLoadingReply(false);
      } else {
        console.error(
          "Failure in setting messages as the data is falsy",
          newMessages
        );
        setLoadingReply(false);
        setErrorOccured(true);
      }
    };

    // * Event for listening api response from GC
    // ** This will get dispatch from messageEventHandler function inside gamecentreService.js file
    window.addEventListener(API_RESPONSE_EVENT, newMessageHandler);
    window.addEventListener(API_SAVE_CHAT_EVENT, onChatSubmit)

    return () => {
      clearTimeout(apiTimeoutRef);
      window.removeEventListener(API_RESPONSE_EVENT, newMessageHandler);
      window.removeEventListener(API_SAVE_CHAT_EVENT, onChatSubmit)
    };
  }, []);

  return (
    <>
    <div className={classNames("relative flex sm:h-[calc(100%-80px)] chat-height-container sm:rounded-lg justify-between gap-10 sm:px-4 sm:px-14 py-9", {
      "bg-white px-4 sm:bg-white": !chatStarted,
      "bg-[#000] pt-0 sm:bg-white": chatStarted
    })}>
      <div className={classNames("absolute bottom-[-100px] left-[-500px] w-[800px] h-[250px] rounded-[800px] bg-[#ac95cf] blur-[256px] sm:hidden", {
        "hidden": !chatStarted
      })} />
      {
        !chatStarted ?
          <ChatInstructions
            chatStarted={chatStarted}
            question={question}
            totalTime={totalTime}
            handleStartChat={handleStartChat}
          />
          : <>
            <ChatInstructions
              chatStarted={chatStarted}
              question={question}
            />
            <div className={`flex sm:h-full flex-1 flex-col justify-between sm:bg-white`}>
              {/* Chat messages wrapper */}
              <div
                id="chat-scroll-section"
                className={classNames(
                  "flex flex-col gap-6 overflow-y-auto px-2 pb-6 sm:h-full"
                )}
              >
                <MobileHeader
                  chatStarted={chatStarted}
                  endChatBtn={endChatBtn}
                  openMobileInstructions={() => {
                    setMobileInstructionsVisible(true)
                  }}
                  totalTime={totalTime}
                  timeLeft={timeLeft}
                />
                  {messages.map((msg, index) => (
                    <MessageBubble
                      key={index}
                      data={msg}
                      wrapperClassName={
                        index === 0 && messages.length - 1 === index ? "mt-[80px] mb-4 sm:mt-auto" : 
                          index === 0 ? "mt-[80px] sm:mt-auto" :
                          messages.length - 1 == index ? "mb-4 sm:mb-0": 
                          null
                      }
                    />
                  ))}
                {loadingReply && (
                  <TypingLoader
                    className={classNames("mb-6 sm:mb-0", {
                      "mt-auto": messages.length === 0,
                    })}
                  />
                )}
                {errorOccured && (
                  <MessageBubble
                    data={{
                      content: "Oops! Something went wrong, please try again.",
                      type: "assistant",
                    }}
                    className={classNames("border bg-red-700  text-white", {
                      "mt-auto": messages.length === 0,
                    })}
                  />
                )}
              </div>
              <div className={classNames(
                "fixed bottom-[1px] mt-20 w-full px-2 py-2 sm:pb-0 sm:px-0 sm:mt-0 sm:static"
              )}>
                <ChatTimeWarningProgress
                  timeLeft={timeLeft}
                />
                {/* Chat input box with send button */}
                <ChatInput
                  ref={inputRef}
                  loadingReply={loadingReply}
                  messages={messages}
                  setMessages={setMessages}
                  getBotReply={getBotReply}
                />
              </div>
            </div>
          </>
      }
      <MobileChatInstructions
        mobileInstructionsVisible={mobileInstructionsVisible}
        question={question}
        closeMobileInstructions={() => {
          setMobileInstructionsVisible(false)
        }}
      />
    </div>
    <SubmitLoader isSubmitting={isSubmitting} />
    </>
  );
};
